import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    getEditVatCodesRoute,
    MERCHANT_ADMIN_SETTINGS_CREATE_VAT_CODES_ROUTE,
    MERCHANT_ADMIN_ROUTE,
} from "../../../../../constants/routes"
import React, { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { MenuItem } from "@mui/material"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { deleteVatCode, fetchMerchantVatCodes, FetchMerchantVatCodesResponse, VatCodesQuery } from "../vatCodesApi"
import { MerchantVatCode } from "../vatCodesTypes"
import { ColumnConfig, columnConfigAlignEnd } from "../../../../../shared/constants/table/config"

function transformData(data: FetchMerchantVatCodesResponse) {
    return data.vatCodes
}

export function VatCodesList() {
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [vatCode, setVatCode] = useState<MerchantVatCode>()

    const vatCodes = useApiQuery(fetchMerchantVatCodes, {
        queryName: VatCodesQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: transformData,
    })

    const { mutate: callDeleteVatCodes } = useApiMutation(deleteVatCode, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [VatCodesQuery.LIST] })
            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse.displayMessage,
            })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(vatCodes)

    const columns = useMemo<MRT_ColumnDef<MerchantVatCode>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("merchant-vat-codes-table-header-vat-code"),
                accessorKey: "code",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("merchant-vat-codes-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.percentageColumn({
                header: l10n.getString("merchant-vat-codes-table-header-percentage"),
                accessorFn: (row) => {
                    const percentage = row.vatPercentage.amount / Math.pow(10, row.vatPercentage.scale)
                    return percentage + "%"
                },
                ...(columnConfigAlignEnd as ColumnConfig<MerchantVatCode>),
            }),
        ],
        [columnFactory, l10n],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<MerchantVatCode>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    navigate(getEditVatCodesRoute(row.original.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="merchant-vat-codes-table-button-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <MenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    setVatCode(row.original)
                    setShowConfirmModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="merchant-vat-codes-table-button-delete">
                    <span>Delete</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="merchant-vat-codes-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_VAT_CODES_LIST}
                    title="VAT codes"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="merchant-vat-codes-create-button">
                            <StandardButton onClick={() => navigate(MERCHANT_ADMIN_SETTINGS_CREATE_VAT_CODES_ROUTE)}>
                                Create
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-vat-codes">
                            <Breadcrumb>VAT codes</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            {/*Confirm delete*/}
            <LocalizedStrict id="merchant-vat-codes-delete-entity-dialog" attrs={{ typeOfEntity: true }}>
                <DeleteConfirmDialog
                    typeOfEntity="VAT code"
                    entityName={vatCode?.name || ""}
                    handleClick={() => {
                        if (!vatCode?.id) throw new Error("VAT code id is undefined")

                        callDeleteVatCodes({ id: vatCode.id })
                    }}
                    onClose={() => setShowConfirmModal(false)}
                    open={showConfirmModal}
                />
            </LocalizedStrict>
        </>
    )
}
